import {
    Box,
    styled,
    Paper,
    TextField,
    Button,
    FormControl,
    Typography,
    MenuItem,
    Select,
    CircularProgress,
    Checkbox,
    Radio,
    IconButton
} from '@mui/material';
import color from 'src/theme/color';
import gradients from 'src/theme/gradients';

export const PageRoot = styled(Box)`
    padding: 104px 324px 64px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100vh;
`;

export const ContentBox = styled(Box)`
    width: 100%;
    max-width: 900px;
`;

export const SectionRoot = styled(Box)`
    width: 100%;
`;

export const SectionTitle = styled(Typography)`
    font:
        600 16px/19px 'Inter',
        sans-serif;
    color: ${color.white90};
    margin-bottom: 12px;
`;

export const SectionSubtitle = styled(Typography)`
    font:
        12px/15px 'Inter',
        sans-serif;
    color: ${color.white50};
    margin-bottom: 20px;
`;

export const SpinnerContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 120px 0;
    gap: 24px;
`;

export const SpinnerText = {
    font: '500 14px/17px "Inter",sans-serif',
    color: color.white50
};

export const spinnerProgressSx = {
    color: color.lightningYellow
};

export const QuestionCard = styled(Paper)`
    width: 100%;
    margin-bottom: 16px;
    background-color: ${color.darkGray};
    border-radius: 12px;
    overflow: hidden;
    box-sizing: border-box;
`;

export const QuestionHeader = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: rgba(255, 255, 255, 0.03);
    }
`;

export const QuestionBody = styled(Box)`
    padding: 0 24px 24px;
`;

export const QuestionText = styled(Typography)`
    font:
        500 14px/18px 'Inter',
        sans-serif;
    color: ${color.white90};
`;

export const QuestionContext = styled(Typography)`
    font:
        13px/17px 'Inter',
        sans-serif;
    color: ${color.white50};
    margin-bottom: 16px;
`;

export const QuestionTitleRow = styled(Box)`
    display: flex;
    align-items: center;
    min-width: 0;
`;

export const SkippedLabel = styled(Typography)`
    color: ${color.white50};
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-style: italic;
    margin-left: 8px;
    flex-shrink: 0;
`;

export const QuestionExpandIcon = styled(IconButton)`
    color: ${color.white50};
    flex-shrink: 0;
    margin-left: 8px;
`;

export const OptionCheckbox = styled(Checkbox)`
    color: ${color.white50};
    &.Mui-checked {
        color: ${color.lightningYellow};
    }
`;

export const OptionRadio = styled(Radio)`
    color: ${color.white50};
    &.Mui-checked {
        color: ${color.lightningYellow};
    }
`;

export const OptionLabel = styled(Typography)`
    color: ${color.white90};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
`;

export const FreeTextField = styled(TextField)`
    margin-top: 8px;
    & .MuiOutlinedInput-root {
        color: ${color.white90};
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        & fieldset {
            border-color: ${color.yellow60};
        }
        &:hover fieldset {
            border-color: ${color.lightningYellow};
        }
        &.Mui-focused fieldset {
            border-color: ${color.lightningYellow};
        }
    }
`;

export const SkipButton = styled(Button)`
    color: ${color.white50};
    text-transform: none;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    border-color: rgba(255, 255, 255, 0.15);
    &:hover {
        border-color: rgba(255, 255, 255, 0.3);
    }
`;

export const SkipButtonRow = styled(Box)`
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
`;

export const SectionCollapseIcon = styled(IconButton)`
    color: ${color.white50};
`;

export const PlanContainer = styled(Box)<{ collapsed?: boolean }>`
    margin-top: 24px;
    width: 100%;
    box-sizing: border-box;
    background-color: ${color.blackRock};
    border-radius: 8px;
    padding: 24px;
    position: relative;
    ${({ collapsed }) =>
        collapsed &&
        `
        max-height: 320px;
        overflow: hidden;
        mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
    `}
`;

export const PlanExpandToggle = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0 0;
    cursor: pointer;
    color: ${color.lightningYellow};
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 500;
    gap: 4px;
    &:hover {
        opacity: 0.8;
    }
`;

export const ActionButtonsRow = styled(Box)`
    display: flex;
    gap: 12px;
    margin-top: 24px;
    justify-content: flex-end;
`;

export const ErrorBox = styled(Box)`
    padding: 24px;
    text-align: center;
`;

export const errorText = {
    fontFamily: '"Inter",sans-serif',
    fontSize: '14px',
    color: '#ff6b6b',
    marginBottom: '16px'
};

export const successText = {
    fontFamily: '"Inter",sans-serif',
    fontSize: '14px',
    color: color.white90,
    marginBottom: '16px'
};

const planningButtonBase = {
    textTransform: 'none' as const,
    fontFamily: '"Inter",sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '100px',
    border: 'none',
    boxShadow: 'none',
    color: color.white,
    '&:hover': { boxShadow: 'none' },
    '&:active': { boxShadow: 'none' }
};

export const OutlinedActionButton = styled(Button)<{ $dimmed?: boolean }>(
    ({ $dimmed }) => ({
        ...planningButtonBase,
        padding: '8px 24px',
        backgroundColor: color.darkGray,
        border: '1px solid rgba(255,255,255,0.18)',
        opacity: $dimmed ? 0.6 : 1,
        pointerEvents: $dimmed ? 'none' : undefined,
        '&:hover': {
            backgroundColor: '#3a3d4a',
            borderColor: 'rgba(255,255,255,0.32)'
        },
        '&.Mui-disabled': {
            color: 'inherit',
            backgroundColor: color.darkGray,
            border: '1px solid rgba(255,255,255,0.18)'
        }
    })
);

export const PrimaryActionButton = styled(Button)({
    ...planningButtonBase,
    padding: '8px 24px',
    background: gradients.goldenYellow,
    '&:hover': { background: gradients.goldenYellow, filter: 'brightness(1.1)' }
});

export const FinalizeButton = styled(Button)({
    ...planningButtonBase,
    paddingLeft: '32px',
    paddingRight: '32px',
    paddingTop: '8px',
    paddingBottom: '8px',
    background: gradients.goldenYellow,
    '&:hover': {
        background: gradients.goldenYellow,
        filter: 'brightness(1.1)'
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.12)',
        color: 'rgba(255,255,255,0.3)'
    }
});

export const StartPlanningButton = styled(Button)({
    ...planningButtonBase,
    padding: '10px 32px',
    background: gradients.goldenYellow,
    '&:hover': { background: gradients.goldenYellow, filter: 'brightness(1.1)' }
});

export const RetryButton = styled(Button)({
    ...planningButtonBase,
    padding: '8px 24px',
    backgroundColor: color.darkGray,
    '&:hover': { backgroundColor: '#3a3d4a' }
});

export const SubmitAnswersButton = styled(Button)({
    ...planningButtonBase,
    padding: '10px 32px',
    background: gradients.goldenYellow,
    '&:hover': { background: gradients.goldenYellow, filter: 'brightness(1.1)' }
});

export const SectionSpacing = styled(Box)({
    marginTop: 24
});

export const FeedbackLabel = styled(Typography)`
    color: ${color.white90};
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    margin-bottom: 8px;
    font-size: 14px;
`;

export const feedbackTextFieldStyles = {
    '& .MuiOutlinedInput-root': {
        color: color.white90,
        fontFamily: '"Inter",sans-serif',
        fontSize: '13px',
        '& fieldset': {
            borderColor: color.yellow60
        },
        '&:hover fieldset': {
            borderColor: color.lightningYellow
        },
        '&.Mui-focused fieldset': {
            borderColor: color.lightningYellow
        }
    }
};

export const StartButtonContainer = styled(Box)`
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

export const ModeFormControl = styled(FormControl)`
    margin-bottom: 20px;
`;

export const ModeLabel = styled(Typography)`
    font:
        600 14px/17px 'Inter',
        sans-serif;
    color: ${color.white90};
    margin-bottom: 8px;
`;

export const ModeMenuItem = styled(MenuItem)`
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: ${color.white90};
    &:hover {
        background-color: rgba(255, 255, 255, 0.06);
    }
    &.Mui-selected {
        background-color: rgba(255, 255, 255, 0.08);
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
    }
`;

export const RecommendedChip = styled('span')`
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: ${color.lightningYellow};
    margin-left: 6px;
    font-weight: 500;
`;

export const ModeSelect = styled(Select)`
    min-width: 220px;
    border-radius: 8px;
    background: ${color.blackRock};
    color: ${color.white90};
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    line-height: 23px;
    & fieldset {
        border-color: ${color.yellow60};
    }
    &:hover fieldset {
        border-color: ${color.lightningYellow};
    }
    &.Mui-focused fieldset {
        border-color: ${color.lightningYellow};
    }
    & .MuiSvgIcon-root {
        color: ${color.white50};
    }
`;

export const StartProgress = styled(CircularProgress)`
    color: ${color.lightningYellow};
`;

export const FinalizeProgress = styled(CircularProgress)`
    color: ${color.darkBlue};
`;

export const modeMenuProps = {
    PaperProps: {
        sx: {
            backgroundColor: color.darkGray,
            borderRadius: '8px',
            border: `1px solid ${color.yellow60}`,
            '& .MuiList-root': { padding: '4px' }
        }
    }
};

export const ViewQuestButton = styled(Button)({
    ...planningButtonBase,
    padding: '10px 32px',
    backgroundColor: color.darkGray,
    '&:hover': { background: gradients.goldenYellow }
});

export const DeleteDraftButton = styled(Button)({
    ...planningButtonBase,
    padding: '8px 24px',
    backgroundColor: color.brightRed,
    color: color.white,
    '&:hover': {
        backgroundColor: color.moderateRed,
        boxShadow: 'none'
    },
    '&&.Mui-disabled': {
        backgroundColor: 'rgba(255, 92, 80, 0.4)',
        color: color.white50
    }
});

export const finalizeProgressSx = {
    color: color.darkBlue
};
